/**
 * Who is signed in, and what the interface should therefore show.
 *
 * Mounted once, above the router. Everything below reads it with `useAuth()`.
 *
 * The session itself is an httpOnly cookie: this holds no token and could not
 * forge one. What it holds is the *answer* to `GET /auth/me` — the user, their
 * permissions, and whether they are the superadmin — which is a convenience for
 * rendering and **never** a control. The server checks every request again.
 */
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { ImpersonatorDto, MeResponse, UserDto } from '@forsys/shared';
import { ApiError, onPasswordChangeRequired, onUnauthorized } from '../api/client';
import * as authApi from '../api/auth';

export interface AuthContextValue {
  /** The signed-in user, or `null` when nobody is. */
  user: UserDto | null;
  /** Permission names the caller holds. Empty for the superadmin — see `can`. */
  permissions: readonly string[];
  /** Bypasses every permission check on the server. `can` reflects that. */
  isSuperadmin: boolean;
  groupIds: readonly number[];
  /**
   * The administrator signed in as `user`, or null. Everything else here is
   * the target's — that is what impersonating means.
   */
  impersonator: ImpersonatorDto | null;
  /** True until the first `GET /auth/me` has answered. Nothing should render before. */
  initialising: boolean;

  /**
   * Whether to *show* something. Never whether to allow it.
   *
   * The superadmin holds no permissions in the ordinary sense — their authority
   * comes from `SUPERADMIN_USER_ID` in the environment, not from a role — so
   * this answers true for them regardless, or the interface would hide from the
   * one account that can do everything.
   */
  can: (permission: string) => boolean;
  /** True if **any** of these is held. For a nav item reachable several ways. */
  canAny: (...permissions: string[]) => boolean;

  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Ends an impersonation; the server signs the administrator back in. The
   * caller then reloads the page, so nothing cached as the target survives.
   */
  stopImpersonating: () => Promise<void>;
  /** Re-reads `GET /auth/me`. Call after anything that changes the caller. */
  refresh: () => Promise<void>;
  /** Replaces the cached user after a profile edit, without a round trip. */
  applyUser: (user: UserDto) => void;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const SIGNED_OUT: MeResponse | null = null;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(SIGNED_OUT);
  const [initialising, setInitialising] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setMe(await authApi.getMe());
    } catch (error) {
      // 401 is the expected answer to "is anybody signed in?" — not a failure.
      // Anything else (the server is down, a proxy returned HTML) is also not a
      // session, and the sign-in screen is the honest thing to show either way.
      if (!(error instanceof ApiError)) throw error;
      setMe(SIGNED_OUT);
    } finally {
      setInitialising(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The cookie can expire, or be revoked from another session — changing your
  // own password revokes every one of them. The app cannot see that happen; it
  // finds out when a request comes back 401, which the client announces here.
  useEffect(() => onUnauthorized(() => setMe(SIGNED_OUT)), []);

  // An administrator required a new password while this session was open. The
  // server now refuses everything else; re-reading `me` brings the flag, and
  // the guard swaps the screen for the change form.
  useEffect(() => onPasswordChangeRequired(() => void refresh()), [refresh]);

  const signIn = useCallback(async (email: string, password: string) => {
    await authApi.login({ email, password });
    // Deliberately not built from the login response: that carries the user but
    // not the permissions, and a half-populated session would render a menu
    // missing everything the user can actually reach.
    setMe(await authApi.getMe());
    setInitialising(false);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      // Signed out locally whatever the server said. A failed logout that left
      // the user looking signed in would be the worse outcome of the two.
      setMe(SIGNED_OUT);
    }
  }, []);

  const stopImpersonating = useCallback(async () => {
    await authApi.stopImpersonation();
  }, []);

  const applyUser = useCallback((user: UserDto) => {
    setMe((current) => (current ? { ...current, user } : current));
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const held = new Set(me?.permissions ?? []);
    const isSuperadmin = me?.isSuperadmin ?? false;
    const can = (permission: string) => isSuperadmin || held.has(permission);

    return {
      user: me?.user ?? null,
      permissions: me?.permissions ?? [],
      isSuperadmin,
      groupIds: me?.groupIds ?? [],
      impersonator: me?.impersonator ?? null,
      initialising,
      can,
      canAny: (...permissions: string[]) => permissions.some(can),
      signIn,
      signOut,
      stopImpersonating,
      refresh,
      applyUser,
    };
  }, [me, initialising, signIn, signOut, stopImpersonating, refresh, applyUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
